import type { ReactNode } from 'react';
import { Check, Globe } from 'lucide-react';
// The wordmark the signed-in app actually uses. `public/boxy-logo.svg` is an
// older cut with an ALPHA badge baked into the artwork — not shippable.
import boxyLogo from '@/assets/boxy_logo.svg';
// Straight from the leaf module, not the `@/config` barrel: that barrel pulls in
// routes.config, which eagerly imports every page — including the ones that
// render this shell. Reading a barrel binding at module scope closes that loop
// and throws "Cannot access 'LOCAL_EN' before initialization" at boot.
import { LOCAL_AR, LOCAL_EN } from '@/config/setup.config';
import useLanguageStore, { type LanguageType } from '@/stores/useLanguageStore';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const LANGUAGES: { key: LanguageType; label: string }[] = [
  { key: LOCAL_EN, label: 'English' },
  { key: LOCAL_AR, label: 'العربية' },
];

interface AuthShellProps {
  /** Card body — the actual sign-in step. */
  children: ReactNode;
  /** Optional line rendered under the card (e.g. the register link). */
  footer?: ReactNode;
}

/**
 * The canvas both auth steps sit on: one glow, one logo, one card.
 *
 * Two layout traps this app sets, both because Tailwind preflight is disabled:
 * `box-border` is required anywhere padding meets a `min-h-screen` (otherwise
 * content-box adds the padding on top of 100vh and the page scrolls), and an
 * `<img>` needs `block` before `mx-auto` will centre it.
 *
 * The glow is horizontally centred on purpose — a light source anchored to one
 * side is the classic RTL tell, and centred needs no mirroring at all.
 */
const AuthShell = ({ children, footer }: AuthShellProps) => {
  const { language, setLanguage } = useLanguageStore();
  const current = LANGUAGES.find((entry) => entry.key === language) ?? LANGUAGES[1];

  return (
    <div className='sd-scope sd-font relative min-h-screen w-full overflow-hidden bg-sd-background'>
      {/* One contained glow sitting behind the card, so the card reads as lit
          rather than as a white block on a yellow wash. */}
      <div
        aria-hidden
        className='pointer-events-none absolute left-1/2 top-1/2 h-[30rem] w-[46rem] -translate-x-1/2 -translate-y-1/2 rounded-[50%] bg-sd-primary/25 blur-[120px]'
      />

      <div className='absolute top-6 z-20 end-6'>
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label='Language'
            className='inline-flex h-9 items-center gap-2 rounded-full border border-solid border-sd-border bg-sd-card/80 px-3.5 text-[13px] font-semibold text-sd-foreground shadow-sm backdrop-blur transition-colors hover:bg-sd-card'
          >
            <Globe className='h-3.5 w-3.5' strokeWidth={2.2} />
            {current.label}
          </DropdownMenuTrigger>
          {/* Portalled to <body>, so it needs its own copy of the token scope. */}
          <DropdownMenuContent
            align='end'
            collisionPadding={16}
            className='sd-scope sd-font min-w-[9.5rem]'
          >
            {LANGUAGES.map((entry) => (
              <DropdownMenuItem
                key={entry.key}
                onClick={() => setLanguage(entry.key)}
                className='cursor-pointer gap-2'
              >
                <span className='flex-1'>{entry.label}</span>
                {language === entry.key ? <Check className='h-4 w-4' strokeWidth={2.5} /> : null}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <main className='relative z-10 box-border flex min-h-screen flex-col items-center justify-center px-4 py-10'>
        <div className='w-full max-w-[440px] animate-in fade-in slide-in-from-bottom-2 duration-500'>
          <img src={boxyLogo} alt='Boxy' className='mx-auto mb-9 block h-12 w-auto' />

          <div className='box-border rounded-[20px] border border-solid border-sd-border bg-sd-card p-8 shadow-[0_1px_2px_rgba(24,24,27,0.04),0_16px_40px_-16px_rgba(24,24,27,0.22)] sm:p-10'>
            {children}
          </div>

          {footer ? <div className='mt-6 text-center text-sm'>{footer}</div> : null}
        </div>
      </main>
    </div>
  );
};

export default AuthShell;
